import type { XbotSuggestion, XbotSuggestSelected } from "@dto/pyxbot-agent.dto";
import { Input, InputRef } from "antd";
import { FC, KeyboardEvent, useEffect, useEffectEvent, useRef, useState } from "react";
import { LuSearch } from "react-icons/lu";
import { v4 as uuidv4 } from "uuid";
import { sendXbotSuggest } from "./client";
import {
  askSearch, moveSearchActive, searchAnswered, SearchState, searchWords, startSearch, SUGGEST_DELAY_MS, SUGGEST_TEXTS,
} from "./flow";
import { optionId, SuggestionList } from "./SuggestionList";

export const OBJECT_SEARCH_TEXTS = {
  placeholder: "Find an object by name…",
  label: "Find an object by name",
  reading: "Searching…",
};

interface Props {
  listId: string;
  /** The word at the cursor when the search opened; "" for none. */
  prefill: string;
  /** Where in the editor's text the pick will go. */
  start: number;
  selected: XbotSuggestSelected | null;
  /** Creates a thread on the fly if the conversation has none yet. */
  getThreadId: () => string | null;
  onThreadAsked: (threadId: string) => void;
  onPick: (item: XbotSuggestion) => void;
  /** The search shuts: on Escape the cursor goes back to the editor (back), on leaving the field it goes where it went. */
  onClose: (back: boolean) => void;
}

// The Objects button's search: a field of its own, so an object can be looked for by name whatever the cursor
// stands on. The arrows walk the objects, Enter puts the one stood on (or the first) in the message.
export const ObjectSearch: FC<Props> = ({ listId, prefill, start, selected, getThreadId, onThreadAsked, onPick, onClose }) => {
  const [text, setText] = useState(prefill);
  const [state, setState] = useState<SearchState>(() => startSearch(uuidv4()));
  const field = useRef<InputRef>(null);
  const first = useRef(true);

  useEffect(() => {
    field.current?.focus({ cursor: "end" });
  }, []);

  // Asks for the words in the field, with the state as it is once the pause is over.
  const ask = useEffectEvent((signal: AbortSignal) => {
    const step = askSearch(state, text, { threadId: getThreadId(), selected, start });
    setState(step.state);
    const sent = step.send;
    if (!sent) {
      return;
    }
    onThreadAsked(sent.thread_id);
    void sendXbotSuggest(sent, signal).then((outcome) => {
      setState((was) => searchAnswered(was, sent, outcome));
    });
  });

  // Asks after a pause in typing (at once for the word the search opened with); a newer ask drops the one still out.
  useEffect(() => {
    const aborter = new AbortController();
    const wait = first.current ? 0 : SUGGEST_DELAY_MS;
    first.current = false;
    const timer = setTimeout(() => ask(aborter.signal), wait);
    return () => {
      clearTimeout(timer);
      aborter.abort();
    };
  }, [text]);

  const items = state.offer?.items ?? [];

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setState((was) => moveSearchActive(was, event.key === "ArrowDown" ? 1 : -1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      // the list on screen is for the words before the last key: wait for the answer to the words in the field
      if (state.status === "reading" || searchWords(text) !== (state.offer?.fragment ?? "")) {
        return;
      }
      const item = items[state.active] ?? items[0];
      if (item) {
        onPick(item);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose(true);
    }
  };

  const activeId = state.active < 0 ? undefined : optionId(listId, state.active);
  const notes = state.status === "short" ? [SUGGEST_TEXTS.tooShort]
    : state.status === "reading" && !state.offer ? [OBJECT_SEARCH_TEXTS.reading]
      : state.status === "unavailable" ? [SUGGEST_TEXTS.unavailable]
        : [];

  return <SuggestionList
    listId={listId}
    offer={state.offer}
    active={state.active}
    onPick={(index) => {
      const item = items[index];
      if (item) {
        onPick(item);
      }
    }}
    label={OBJECT_SEARCH_TEXTS.label}
    moreNotes={notes}
    head={<Input
      ref={field}
      size="small"
      className="xm-chat-object-search-field"
      prefix={<LuSearch aria-hidden="true" />}
      placeholder={OBJECT_SEARCH_TEXTS.placeholder}
      aria-label={OBJECT_SEARCH_TEXTS.label}
      role="combobox"
      aria-autocomplete="list"
      aria-expanded={Boolean(state.offer)}
      aria-controls={listId}
      aria-activedescendant={activeId}
      value={text}
      maxLength={200}
      onChange={(event) => setText(event.target.value)}
      onKeyDown={onKeyDown}
      onBlur={() => onClose(false)}
    />}
  />;
};
